import React, { Fragment, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { SubmitButton } from 'Components/Fields/Buttons';
import { SubmitError } from 'Components/Fields/SubmitError';
import { onSubmitReactFormError } from 'Utils/FormValidation';
import { verifyAddress } from 'Api/AccountApi';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { logger } from 'Utils/Logger';
import { ShippingFormVerificationModal } from './ShippingFormVerificationModal';
import { ShippingFormFields } from './ShippingFormFields';
import { useForm } from 'Hooks/useForm';

import './ShippingForm.css';

export const ShippingForm = ({ onFormSubmit, showDisclaimer = false, isLoading }) => {
  const formName = 'shippingForm';
  const { formFieldProps, submitting, submitStatus, submitDisabled, formError, setFormError, handleSubmit } = useForm({
    onFormSubmit,
    formName,
  });
  const submitSucceeded = submitStatus === 'success';
  const [showModal, toggleModal] = useState(false);
  const [addresses, setAddresses] = useState({});

  const { isGifting } = useSelector((s) => s.purchaseMode);
  const { selectedPlan } = useSelector((s) => s.plans);

  useEffect(() => {
    if (showModal) {
      trackEvent(TRACKING_ENUMS.EVENT_SHIPPING_ADDRESS_VERIFICATION_STARTED);
    }
  }, [showModal]);

  const onSuccess = (event, confirmedAddress, inVerificationModal = false) => {
    logger.started('confirm shipping address', confirmedAddress);
    // creates a new object inside the form -> shipping.values
    const verifiedAddress = {
      ...confirmedAddress,
      country: 'US',
    };
    if (!isGifting && inVerificationModal) {
      const packageName = selectedPlan ? selectedPlan.packageName : '';
      trackEvent(TRACKING_ENUMS.EVENT_SHIPPING_ADDRESS_VERIFICATION_COMPLETED, {
        page_start_source: 'plan module',
        page_plan_name: packageName,
      });
    }
    logger.completed('confirm shipping address', verifiedAddress);
    for (const field in verifiedAddress) {
      formFieldProps.updateValue(field, verifiedAddress[field]);
    }
    handleSubmit(event);
    toggleModal(false);
  };

  /**
   * Makes a call to verify an address
   * Dispatches a built-in action to add a new property to the form
   * with an address data corrected by the API
   * OR
   * Throws an error with the error and message data from the API
   * @param { object } submittedAddress
   * @returns {Promise<void>}
   */
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    const submittedAddress = formFieldProps.fieldValues;
    logger.started('verify shipping address', submittedAddress);
    const { isValid, corrected, errors, message = '', address = {} } = await verifyAddress(submittedAddress);
    if (errors || message) {
      logger.error('verify shipping address', submittedAddress, errors || message);
      for (const field in errors) {
        formFieldProps.updateError(field, errors[field]);
      }
      const formError = onSubmitReactFormError(message, null, errors);
      setFormError(formError);
      return;
    }

    if (isValid) {
      const { firstName, lastName } = submittedAddress;
      const verifiedAddress = {
        firstName,
        lastName,
        ...address,
      };
      if (corrected) {
        const addresses = {
          verified: verifiedAddress,
          submitted: submittedAddress,
        };
        logger.completed('verify shipping address', addresses);
        setAddresses(addresses);
        toggleModal(true);
      } else {
        logger.completed('verify shipping address', verifiedAddress);
        onSuccess(event, verifiedAddress);
      }
    }
  };

  return (
    <Fragment>
      <form name={formName} onSubmit={handleFormSubmit}>
        <div className={`shippingForm ${(submitSucceeded && 'submitSucceeded') || ''}`}>
          <ShippingFormFields formFieldProps={formFieldProps} />
          <SubmitError error={formError} formName={formName} trackError={false} />
          <SubmitButton
            text={isGifting ? 'Submit' : 'Continue'}
            isDisabled={submitDisabled}
            submitting={submitting || isLoading}
          />
          {showDisclaimer && (
            <div className="shippingDisclaimer">
              <div className="shippingDisclaimerTitle">Make sure you enter an accurate address.</div>
              <div className="shippingDisclaimerDescription">
                If the address is not entered correctly, your package will be returned as undeliverable. Save time and
                frustration by entering address information in the appropriate boxes and double-checking for typos and
                other errors.
              </div>
            </div>
          )}
        </div>
      </form>
      <ShippingFormVerificationModal
        isOpen={showModal}
        onClose={() => toggleModal(false)}
        addressMap={addresses}
        onContinue={onSuccess}
      />
    </Fragment>
  );
};

export default ShippingForm;
